<template>
  <AgGridVue
    v-bind="gridOptions"
    class="ag-theme-alpine"
    :context="tableContext"
  />
</template>

<script setup>
import { readonly, watch } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { ModuleRegistry } from 'ag-grid-community'
import { ServerSideRowModelModule } from 'ag-grid-enterprise'
import { useResetOnSort } from '@/Components/DataGrid/Composables/reset-on-sort'
import { useHttpRequest } from '@/Components/DataGrid/Composables/http-request'
import { useColumnStateManagement } from '@/Components/DataGrid/Composables/column-state'
import { useFilterStateManagement } from '@/Components/DataGrid/Composables/filter-state'
import { useCheckBoxColumn } from '@/Components/DataGrid/Composables/checkbox-column'
import { usePreFilters } from '@/Components/DataGrid/Composables/prefilters'
import { useGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useNavigation } from '@/Components/Navigation/navigation'
import { useGridApi } from '@/Components/DataGrid/grid-api'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  endpoint: {
    type: String,
    required: true
  },
  /* Filter data that will be forwarded on any AG Grid request. */
  filters: {
    type: Object,
    default: () => {}
  },
  /* AG Grid column configuration for the data set. */
  columns: {
    type: Array,
    required: true
  },
  /* How many rows to fetch and cache per request */
  blockSize: {
    type: Number,
    default: 50
  },
  /* How many rows to display per page */
  pageSize: {
    type: Number,
    default: 50
  },
  /* Whether to show the checkbox column */
  showCheckColumn: {
    type: Boolean,
    default: false
  },
  /* Sidebar option override. False to hide sidebar completely. */
  sideBar: {
    type: [Object, Boolean],
    default: undefined
  },
  /* Pass custom context to AG Grid component. */
  tableContext: {
    type: Object,
    default: {}
  },
  /* Set grid autosize strategy */
  autosizeConfig: {
    type: Object,
    default: null
  },
  /* Set initial state for the grid */
  initialState: {
    type: Object,
    default: () => {}
  }
})

const { gridApi, createGridOptions } = useGridApi()
const gridApiReadOnly = readonly(gridApi)

const { resetOnSort } = useResetOnSort()

const { httpRequest } = useHttpRequest({
  endpoint: props.endpoint,
  filters: props.filters
})

const { mode } = useNavigation()

usePreFilters({
  gridApi: gridApiReadOnly,
  filters: props.filters
})

useColumnStateManagement({
  gridApi: gridApiReadOnly,
  name: props.name
})

useFilterStateManagement({
  gridApi: gridApiReadOnly
})

if (props.showCheckColumn) {
  useCheckBoxColumn({ gridApi })
}

const { context } = useGridState()

context.refresh = () => {
  gridApi.value.refreshServerSide({ purge: true })
}

const serverSideDatasource = {
  async getRows(params) {
    const isResetting = resetOnSort.getRowsPreHandler(params)
    if (isResetting) return
    await httpRequest.fetchData(params)
  }
}

watch(mode, () => {
  if (props.autosizeConfig) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit()
    }, 250)
  }
})

const defaultSidebar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',

      // Full reference: https://www.ag-grid.com/javascript-data-grid/tool-panel-columns/
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: true,
        suppressColumnSelectAll: true
      }
    }
  ]
}

const gridOptions = createGridOptions({
  onGridReady: (params) => {
    gridApi.value = params.api
    context.gridApi = gridApi
  },
  getRowId: (params) => params.data.id,

  rowModelType: 'serverSide',
  serverSideDatasource,

  rowSelection: 'multiple',

  columnDefs: props.columns,

  defaultColDef: {
    resizable: true,
    sortable: true,
    menuTabs: ['filterMenuTab'],
    floatingFilter: true,
    suppressHeaderMenuButton: true
  },

  autoSizeStrategy: props.autosizeConfig,

  pagination: true,
  paginationPageSizeSelector: false,
  paginationPageSize: props.pageSize,
  cacheBlockSize: props.blockSize,
  unSortIcon: true,

  enableRangeSelection: true,

  sideBar: props.sideBar ?? defaultSidebar,

  initialState: props.initialState
})
</script>
